import { Component, Inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RoutePageComponent } from '../../shared/route-page/route-page.component';
import { InformationCardComponent } from '../../shared/information-card/information-card.component';
import { FooterImageComponent } from '../../shared/footer-image/footer-image.component';
import { Field, IField, AlertComponent } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { DataStoreService } from '../../../services/data-store.service';
import { globalConstants } from '../../shared/global-constants';
import { Router } from '@angular/router';
import { PaymentService } from '../../../services/payment-portal.service';
import { PaymentNavigationService } from '../../../services/payment-navigation.service';
import { IPayload, PolicyDetails } from '../../../services/base/policy-details';
import { NavigationFooterComponent } from '../../shared/navigation-footer/navigation-footer.component';
import { CommonModule, DatePipe } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { APP_CONFIG, AppConfig } from '../../../../app.config';
import { ProductNamePipe } from '../../pipes/productName.pipe';
import { map } from 'rxjs';

@Component({
    selector: 'app-payment-center',
    imports: [
        RoutePageComponent,
        FooterImageComponent,
        InformationCardComponent,
        NavigationFooterComponent,
        CommonModule,
        AlertComponent
    ],
    templateUrl: './payment-center.component.html',
    styleUrls: ['./payment-center.component.scss']
})
export class PaymentCenterComponent implements OnInit {
    globalConstants = globalConstants;
    referenceNumberType = signal('');
    referenceNumberValue = signal('');
    isMobile = toSignal(
        this.breakpointObserver.observe(['(max-width: 768px)']).pipe(map(r => r.matches)),
        { initialValue: this.breakpointObserver.isMatched('(max-width: 768px)') }
    );
    policyData = signal<any>(null);
    policies = signal<any[]>([]);
    policyDataField = signal<IField | null>(null);
    loaded = signal(false);
    paymentDetails = signal<IPayload | null>(null);
    noOutstandingBalance = signal(false);
    disablePaymentOption = signal(false);
    showPaymentButton = signal(false);
    referenceCategoryMap: Record<string, string> = {
        PolicyNumber: 'Policy number',
        InvoiceNumber: 'Invoice number',
        AccountNumber: 'Account number'
    };
    disablePaymentAlert = signal('');
    datePipe = new DatePipe('en-US');

    constructor(
        private router: Router,
        private dataStore: DataStoreService,
        private paymentService: PaymentService,
        private paymentNavigationService: PaymentNavigationService,
        @Inject(APP_CONFIG) private config: AppConfig,
        private breakpointObserver: BreakpointObserver
    ) {
        const navState = this.paymentNavigationService.navigationState();
        this.referenceNumberType.set(this.referenceCategoryMap[navState?.category || ''] || '');
        this.referenceNumberValue.set(navState?.search || '');
    }

    ngOnInit() {
        this.dataStore.getPageData('payment-center').subscribe((pageData) => {
            const details = this.paymentService.getPaymentDetails();
            this.paymentDetails.set(details);
            this.policyDataField.set(new Field(pageData.getField('policyDetails')));
            this.pagePreProcess();
            this.loaded.set(true);
        });
    }

    pagePreProcess(): void {
        const details = this.paymentDetails();
        const policy = details?.retrievePolicyResponse?.details.policyDetails?.at(-1);
        this.policyData.set(policy);

        const field = this.policyDataField()!;
        field.caption = new ProductNamePipe().transform(policy?.lob || '');
        field.icon_path = 'products/' + field.caption;
        field.description = policy?.insuredAddress + ', ' + policy?.insuredCity + ', ' + policy?.insuredState + ' ' + policy?.insuredZip;

        if (Number(details?.retrievePolicyResponse?.details.accountDetail.outstandingBalance) === 0){
            this.noOutstandingBalance.set(true);
            this.showPaymentButton.set(true);
            this.policies.set(this.config.interestedProtectionPlans.map((plan: any) => ({
                ...plan,
                icon: `assets/images/${plan?.product?.toLowerCase()}.svg`
            })));
        }

        if (details?.retrievePolicyResponse?.details.accountDetail.disablePayment) {
            this.disablePaymentOption.set(true);
            this.showPaymentButton.set(true);
            this.policies.set(this.config.interestedProtectionPlans.map((plan: any) => ({
                ...plan,
                icon: `assets/images/${plan?.product?.toLowerCase()}.svg`
            })));
        }

        this.updatePolicyDataField();
    }

    updatePolicyDataField() {
        const field = this.policyDataField()!;
        const details = this.paymentDetails();
        let termTemplate = field.options.optionsList[0];
        field.options.optionsList = [];
        details?.retrievePolicyResponse?.details.policyDetails.forEach((policy: PolicyDetails) => {
            let errorType = false;
            let termField = new Field({
                name: `Term-${policy.term}`,
                options: {
                    optionsList: termTemplate.options.optionsList
                }
            });

            termField.options.optionsList.forEach((displayField: any) => {
                const fieldName = displayField.name;
                if (fieldName && policy.hasOwnProperty(fieldName)) {
                    const key = fieldName as keyof PolicyDetails;
                    const value = policy[key];
                    displayField.value = value !== null && value !== undefined && value !== '' ? value : '-';
                    if (fieldName === 'autopay' && displayField.value === 'Enrolled') {
                        errorType = true;
                    }
                } else if (fieldName.includes('policyStatusTag')) {
                    displayField.value = policy.tag !== '' ? policy.tag : policy.policyStatus;
                } else if (fieldName.includes('termPeriod')) {
                    displayField.value =
                        policy?.effectiveDate && policy?.expirationDate
                            ? `${this.datePipe.transform(policy?.effectiveDate, 'MM/dd/yyyy')} - ${this.datePipe.transform(policy?.expirationDate, 'MM/dd/yyyy')}` : '-';
                } else if (fieldName.includes('dueDate')) {
                    displayField.value = policy?.paymentDue ? `${this.datePipe.transform(policy?.paymentDue, 'MM/dd/yyyy')}` : '-';
                } else if (fieldName.includes('displayNote')) {
                    if (details?.retrievePolicyResponse?.details.accountDetail.disablePayment) {
                        if (errorType) {
                            displayField.value = '';
                            this.disablePaymentAlert.set(policy?.note || '');
                        } else {
                            this.disablePaymentOption.set(false);
                            displayField.type = errorType ? 'neutralBlue' : 'error';
                            displayField.value = policy?.note || '-';
                        }
                    } else {
                        displayField.type = errorType ? 'neutralBlue' : 'error';
                        displayField.value = policy?.note || '-';
                    }
                } else {
                    displayField.value = '-';
                }

                if (displayField.value !== '-' && ['amountDue', 'outstandingBalance'].includes(fieldName)) {
                    displayField.value = `$${Number(displayField.value).toFixed(2)}`;
                }
            });

            field.options.optionsList.push(termField);
        });
    }

    goToPage(item: any) {
        if (item?.linkURL) {
            window.open(item.linkURL, '_blank');
        }
    }

    makePayment(event: any): void {
        if (event === 'goBack') {
            this.router.navigate(['/paymentportal']);
        } else if (event === 'continue') {
            this.router.navigate(['/make-payment']);
        }
    }
}
